import { FC } from 'react';
import { BodyText, Card, CircleButton, Icon, withStyles } from 'vopay';

import Dialog from '#shared/components/Dialog/Dialog';
import styles from './UpgradePlanCardDialog.module.scss';
import { useTranslation } from 'react-i18next';

import { Button, Title } from '#shared/components/UI';

type UpgradePlanCardDialogProps = {
  onCancel: () => void;
  // Captured from useHostNavigate() by the opener — the dialog renders under
  // the DialogProvider, above the HostNavigationProvider, so the context
  // can't be read here directly.
  hostNavigate?: (path: string, state?: unknown) => void;
};

const cn = withStyles(styles);

const UpgradePlanCardDialog: FC<UpgradePlanCardDialogProps> = ({ onCancel, hostNavigate }) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Shared.Customers.PaymentMethodsTab.Details.Verification.UpgradePlanCardDialog',
  });

  return (
    <Card className={cn('modalContainer')}>
      <Dialog.Title level="3">{t('title', { defaultValue: 'Upgrade Your Card Plan!' })}</Dialog.Title>

      <div className={cn('closeButton')}>
        <CircleButton icon="close" size="large" circleFill onClick={onCancel} />
      </div>

      <BodyText className={cn('description')}>
        {t('description', {
          defaultValue:
            'Confirm ownership and registration details retrieved from card processor records for this contact.',
        })}
      </BodyText>

      <div className={cn('plans')}>
        <div className={cn('plan', 'basic')}>
          <Icon icon="checkBlueGradient" width="80px" height="80px" color="none" />

          <div>
            <Title level="5">{t('basicTitle', { defaultValue: 'Basic' })}</Title>

            <BodyText className={cn('text')}>
              {t('basicDescription', { defaultValue: 'Verify the ownership of the card holder.' })}
            </BodyText>
          </div>
        </div>

        <div className={cn('plan', 'enhanced')}>
          <Icon icon="iconsEnhancedCard" width="80px" height="80px" color="none" />

          <div>
            <Title className={cn('title')} level="5">
              {t('enhancedTitle', { defaultValue: 'Enhanced' })}
            </Title>

            <BodyText className={cn('text')}>
              {t('enhancedDescription', {
                defaultValue: 'Confirm ownership and access registration details retrieved from card processor.',
              })}
            </BodyText>
          </div>
        </div>
      </div>

      <Button
        className={cn('button')}
        colorScheme="primary"
        onClick={() => {
          onCancel();

          // Link-out via the host's onNavigate; the dialog just closes when the
          // host can't navigate. (dropped router state: ticketSubject)
          hostNavigate?.('/create-tickets', {
            ticketSubject: t('ticketSubject', { defaultValue: 'Enquiry about Card verification' }),
          });
        }}
      >
        {t('contactSupportButton', { defaultValue: 'Contact Support' })}
      </Button>
    </Card>
  );
};

export default UpgradePlanCardDialog;
